import * as React from "react";

import { assert } from "$app/utils/assert";
import { cast } from "$app/utils/cast";

type TimeZone = { name: string; offset: number };

export type CurrentSeller = {
  id: string;
  email: string | null;
  name: string | null;
  subdomain: string | null;
  avatarUrl: string;
  isBuyer: boolean;
  timeZone: TimeZone;
  has_published_products: boolean;
  can_publish_products: boolean;
  publishBlockedReason: "no_payout_method" | "payout_setup_rejected" | null;
  noPayoutRailInComplianceCountry: boolean;
  legalGuardianRequirementMet: boolean;
  legalGuardianUnsupported: boolean;
  isNameInvalidForEmailDelivery: boolean;
  profileBackgroundColor: string;
  profileHighlightColor: string;
  profileFont: string;
};

const Context = React.createContext<CurrentSeller | null | undefined>(undefined);

export const parseCurrentSeller = (data: unknown): CurrentSeller | null => {
  const parsed = cast<{
    id: string;
    email: string | null;
    name: string | null;
    subdomain: string | null;
    avatar_url: string;
    is_buyer: boolean;
    time_zone: TimeZone;
    has_published_products: boolean;
    can_publish_products: boolean;
    publish_blocked_reason: "no_payout_method" | "payout_setup_rejected" | null;
    no_payout_rail_in_compliance_country: boolean;
    legal_guardian_requirement_met: boolean;
    legal_guardian_unsupported: boolean;
    is_name_invalid_for_email_delivery: boolean;
    profile_background_color: string;
    profile_highlight_color: string;
    profile_font: string;
  } | null>(data);
  if (parsed == null) return null;
  return {
    id: parsed.id,
    email: parsed.email,
    name: parsed.name,
    subdomain: parsed.subdomain,
    avatarUrl: parsed.avatar_url,
    isBuyer: parsed.is_buyer,
    timeZone: parsed.time_zone,
    has_published_products: parsed.has_published_products,
    can_publish_products: parsed.can_publish_products,
    publishBlockedReason: parsed.publish_blocked_reason,
    noPayoutRailInComplianceCountry: parsed.no_payout_rail_in_compliance_country,
    legalGuardianRequirementMet: parsed.legal_guardian_requirement_met,
    legalGuardianUnsupported: parsed.legal_guardian_unsupported,
    isNameInvalidForEmailDelivery: parsed.is_name_invalid_for_email_delivery,
    profileBackgroundColor: parsed.profile_background_color,
    profileHighlightColor: parsed.profile_highlight_color,
    profileFont: parsed.profile_font,
  };
};

export const useCurrentSeller = (): CurrentSeller | null => {
  const value = React.useContext(Context);
  assert(value !== undefined, "Cannot read current seller, make sure CurrentSellerProvider is used");
  return value;
};

export const CurrentSellerProvider = Context.Provider;
